import { Hourglass } from 'lucide-react';

import { EXAM_TYPE_LABEL } from '@/features/teacher/exams/constants';
import { formatDate, formatTimeRange } from '@/lib/format';
import { cn } from '@/lib/utils';
import type { FamilyExamPaper } from '@/types/student';

interface AwaitingResultsListProps {
  /** Sat before today, no mark for this child yet — most recent first, as the server sent them. */
  papers: FamilyExamPaper[];
  /** Who the marks are for, in the line under the heading: "your", "Aarav's". */
  whose: string;
  className?: string;
}

/**
 * The papers a child has already sat that no teacher has marked for them yet.
 *
 * Shared by the student's marks screen and the parent's Results tab. Without it
 * a paper dropped off Upcoming Exams the day after it was sat and did not reach
 * the scorecard until it was marked, so for that stretch it showed nowhere.
 *
 * Kept apart from the scorecard on purpose: "not marked yet" must never read as
 * a low score or a missing one. Renders nothing when there is nothing waiting.
 */
export function AwaitingResultsList({ papers, whose, className }: AwaitingResultsListProps) {
  if (papers.length === 0) return null;

  return (
    <section
      className={cn('card p-4 sm:p-5', className)}
      aria-labelledby="awaiting-results-heading"
    >
      <div className="flex items-start gap-3">
        <span
          className="grid h-9 w-9 shrink-0 place-items-center rounded-card bg-warn-bg text-warn"
          aria-hidden
        >
          <Hourglass className="h-4 w-4" />
        </span>
        <div className="min-w-0">
          <h2 id="awaiting-results-heading" className="text-base font-bold text-ink">
            Completed exams · results awaited ({papers.length})
          </h2>
          <p className="mt-0.5 text-sm text-ink-2">
            These papers have been sat. {whose} marks appear on the scorecard as soon as a teacher
            enters them.
          </p>
        </div>
      </div>

      <ul className="mt-4 divide-y divide-line border-t border-line">
        {papers.map((paper) => (
          <li
            key={paper.uuid}
            className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-3"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-ink">
                {paper.subject.name}
                <span className="font-normal text-ink-2"> · {paper.name}</span>
              </p>
              <p className="mt-0.5 text-sm text-ink-3">
                {formatDate(paper.examDate)}
                {paper.startTime && ` · ${formatTimeRange(paper.startTime, paper.endTime, '')}`}
                {` · ${EXAM_TYPE_LABEL[paper.examType]} · out of ${paper.maxMarks}`}
              </p>
            </div>
            <span className="pill bg-warn-bg text-warn">Marks awaited</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
